import React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import type { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { BreadcrumbJsonLd } from 'next-seo';
import { get } from 'lodash';
import { useQuery } from '@apollo/client';

import SEOTags from '~/components/SEOTags';
import { Identifiers, PATH, SectionsKey } from '~/constants/enum';
import { QUERY_GET_COLLECTIONS } from '~/data/collections';
import { GET_SCHEMA_COLLECTION } from '~/data/schemas/schema.gql';
import { useGetFilterValues } from '~/hooks/useGetFilterValues';
import useRenderComponent from '~/hooks/useRenderComponent';
import Layout from '~/layouts/ThemeLayout';
import CollectionSchema from '~/schema/CollectionSchema';
import { ProductListProps } from '~/sections/ProductList/ProductList';
import { Product } from '~/services/product';
import { trackingCategory } from '~/services/tracking';
import { initializeApollo } from '~/utils/apollo-client';
import { converShortCodeFilterKey, convertCleanFilterKey } from '~/utils/converters';
import { createSortObject } from '~/utils/filter';
import { getPageStruct, renderSectionNew } from '~/utils/pageTwo';
import { getTheme } from '~/utils/theme';
import { getUrlByTheme, getUrlCustom } from '~/utils/theme';
import { transformProductItems } from '~/utils/transform';
import { QUERY_GET_MEGA_MENU_GQL } from '~/data/mega-menu/gql';
import { GET_STORE_CONFIG_ON_DOCUMENT } from '~/data/home';

const pageStruct = getPageStruct(getTheme(), 'collection-detail');

const sections: any = pageStruct?.children
    ?.sort((a: any, b: any) => a.order - b.order)
    .map((item: any, index: number) => {
        const Section = renderSectionNew(item);

        return {
            id: item.id,
            order: item.order,
            dataKey: item.dataKey,
            component: Section
        };
    });
const CollectionDetailPage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const { slug, configSchema, sortFields, filterData, dataMegaMenu, storeConfig } = props;

    const router = useRouter();
    const { query } = router;

    //Filter Values
    const { filterValues, pageSize = 30, currentPage = 1, sort } = useGetFilterValues();
    const pagination = {
        pageSize: pageSize,
        currentPage
    };
    //Products
    const { data: products, loading } = useQuery(QUERY_GET_COLLECTIONS, {
        variables: {
            page_size: pagination.pageSize,
            current_page: pagination.currentPage,
            url_key: slug,
            filter: convertCleanFilterKey(filterValues, filterData?.map((i: any) => i?.attribute_code) || []),
            sort: sort ?? { relevance: 'DESC' }
        },
        // nextFetchPolicy: "network-only",
        context: { fetchOptions: { method: 'GET' } },
        skip: !slug
    });

    const productCollection: Product[] = useMemo(() => {
        if (!!loading)
            // return Array.from({ length: pageSize }).map(
            //   (_, index) => ({ id: index.toString() } as Partial<Product>)
            // );
            return [];

        return transformProductItems(get(products, 'productCollection[0].items', []));
    }, [products, loading]);

    const initTrackingcategory = useRef(false);
    useEffect(() => {
        if (!!initTrackingcategory.current) return;
        trackingCategory({
            keyword: slug,
            data: productCollection as Product[]
        });
        initTrackingcategory.current = true;
    }, [slug, productCollection]);

    const nameCollection = useMemo(() => {
        if (loading) return slug;

        return get(products, 'productCollection[0].name', slug);
    }, [products, loading, slug]);

    const total = useMemo(() => get(products, 'productCollection[0].total_count', 0), [products]);
    const description = useMemo(() => get(products, 'productCollection[0].description', ''), [products]);
    const campaign_id = useMemo(() => get(products, 'productCollection[0].campaign_id', null), [products]);

    const sectionData = useMemo(() => {
        return {
            [SectionsKey.ProductList]: {
                breadcrumbs: [
                    { title: 'Home', path: '/' },
                    { title: 'Collections', path: PATH.Collections },
                    { title: nameCollection }
                ],
                title: nameCollection,
                data: productCollection,
                filters: {
                    aggregations: filterData,
                    sortFields: sortFields
                },
                isShowFilter: query?.filter === 'show',
                total,
                isLoading: loading,
                defaultSort: 'relevance-DESC',
                identifier: 'collection',
                listKey: campaign_id
            } as ProductListProps,
            [SectionsKey.CollectionsContent]: {
                data: description
            }
        };
    }, [campaign_id, filterData, productCollection, total, sortFields, loading, description]);

    const { meta_title, meta_description, url_key, name } = props?.metaCollections;
    const dataCarouselJsonLd: any[] =
        !!props.metaCollections?.items?.length &&
        props.metaCollections?.items?.map(({ url_key, name, thumbnail, price_range }: any) => {
            return {
                keywords: url_key,
                thumbnailUrl: thumbnail?.url,
                image: thumbnail?.url,
                name,
                about: name,
                availability: 'instock',
                offers: {
                    price: price_range?.minimum_price?.final_price?.value,
                    priceCurrency: price_range?.minimum_price?.final_price?.currency,
                    url: getUrlByTheme(PATH.ProductDetail.replace('[slug]', url_key))
                }
            };
        });
    const itemListElements = [
        {
            position: 1,
            name: 'Home',
            item: process.env.NEXT_PUBLIC_URL + ''
        },
        {
            position: 2,
            name: meta_title,
            item: getUrlByTheme(`/collections/${slug}`)
        }
    ];

    const { render } = useRenderComponent({
        sectionData: sectionData,
        fileName: 'collection-detail'
    });

    return (
        <>
            <SEOTags
                title={meta_title || name}
                description={meta_description || name}
                keywords={url_key}
                url={getUrlByTheme(`/collections/${slug}`)}
                type="product.group"
            >
                <link
                    rel="canonical"
                    href={getUrlCustom({
                        pathname: process.env.NEXT_PUBLIC_URL + PATH.CollectionDetail,
                        query: {
                            slug: props?.metaCollections?.url_key
                        }
                    })}
                    data-vm-ssr="true"
                />
            </SEOTags>
            <CollectionSchema
                config={configSchema}
                dataMeta={{
                    name: meta_title || name,
                    description: meta_description || name,
                    isRelatedTo: dataCarouselJsonLd || []
                }}
            />

            {!!itemListElements.length && <BreadcrumbJsonLd itemListElements={itemListElements} />}

            <Layout dataMegaMenu={dataMegaMenu} storeConfig={storeConfig}>
                <div className="flex flex-col gap-8">
                    {sections?.map((item: any, index: number) => {
                        const Section = item.component;

                        return (
                            <React.Fragment key={`section-${index}`}>
                                {Section ? <Section {...get(sectionData, item.dataKey, {})} /> : null}
                            </React.Fragment>
                        );
                    })}
                </div>
            </Layout>
        </>
    );
};

export default CollectionDetailPage;

export async function getServerSideProps({ params, query }: GetServerSidePropsContext) {
    const apolloClient = initializeApollo({
        httpLinkProps: { useGETForQueries: true }
    });

    try {
        //Config
        const { data: dataSchema } = await apolloClient.query({
            query: GET_SCHEMA_COLLECTION
        });
        const filter = converShortCodeFilterKey(query as any, 'decode');

        //Products
        const { data: productsResponse } = await apolloClient.query({
            query: QUERY_GET_COLLECTIONS,
            variables: {
                url_key: params?.slug as string,
                page_size: query?.pageSize || 30,
                current_page: query?.currentPage || 1,
                filter: convertCleanFilterKey(filter, {}) || {},
                sort: createSortObject(query.sort as string, '-') || {
                    relevance: 'DESC'
                }
            }
        });

        const metaData = {
            metaCollections: {
                meta_title: '',
                meta_description: '',
                url_key: '',
                name: ''
            } as any
        };

        metaData.metaCollections = get(productsResponse, 'productCollection[0]', {});
        const sortFields = get(productsResponse, 'productCollection[0].sort_fields', []);
        const filterData = get(productsResponse, 'productCollection[0].aggregations', []);

        const { data: dataMegaMenu } = await apolloClient.query({
            query: QUERY_GET_MEGA_MENU_GQL,
            variables: {
                identifiers: Identifiers.MainMenu,
            },
            context: { fetchOptions: { method: 'GET' } }
        });
        const response = await apolloClient.query({
            query: GET_STORE_CONFIG_ON_DOCUMENT
        });
        const storeConfig = get(response, 'data.storeConfig', {
            frontend_config_logo: null,
        });

        return {
            props: {
                slug: params?.slug as string,
                configSchema: dataSchema?.MarketingConfig?.schemaCollection,
                sortFields,
                filterData,
                initialApolloState: apolloClient.cache.extract(),
                dataMegaMenu,
                storeConfig,
                ...metaData
            }
        };
    } catch (error) {
        console.log(error);

        return {
            notFound: true
        };
    }
}
